/**
 * @fileoverview Endpoint Status Panel
 * @description Displays one HEAD or health endpoint's schedule and latest result.
 */

import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Modal, Stack, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { endpointExists } from '../hooks/useAzureFunctionHealth';
import { PROBE_KINDS } from '../constants';

const RESPONSE_PREVIEW_LENGTH = 30;
const TOOLTIP_CURSOR_OFFSET = 16;

/**
 * Render a shortened response body with pointer-appropriate full-text access.
 * Desktop users receive a cursor-offset tooltip; touch users receive a modal overlay.
 * @param {Object} props - Response text and accessible endpoint label.
 * @returns {JSX.Element} Interactive response body preview.
 */
function ResponseBodyPreview({ fullText, endpointLabel }) {
  const [supportsHover, setSupportsHover] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const isTruncated = fullText.length > RESPONSE_PREVIEW_LENGTH;
  const previewText = isTruncated
    ? `${fullText.slice(0, RESPONSE_PREVIEW_LENGTH)}...`
    : fullText;

  useEffect(() => {
    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const updatePointerMode = () => setSupportsHover(hoverQuery.matches);
    updatePointerMode();
    hoverQuery.addEventListener?.('change', updatePointerMode);
    return () => hoverQuery.removeEventListener?.('change', updatePointerMode);
  }, []);

  /**
   * Place the tooltip away from the cursor and toward the open viewport quadrant.
   * Fixed positioning and disabled pointer events prevent hover flicker when the
   * tooltip overlaps a neighboring widget.
   * @param {React.MouseEvent} event - Pointer position over the response box.
   * @returns {void}
   */
  const positionTooltip = (event) => {
    if (!supportsHover || !isTruncated) {
      return;
    }

    const responseBounds = event.currentTarget.getBoundingClientRect();
    const placeLeft = responseBounds.left + responseBounds.width / 2 > window.innerWidth / 2
      || event.clientX > window.innerWidth - 380;
    const placeAbove = responseBounds.top + responseBounds.height / 2 > window.innerHeight / 2
      || event.clientY > window.innerHeight - 180;

    setTooltipPosition({
      horizontalProperty: placeLeft ? 'right' : 'left',
      horizontalValue: placeLeft
        ? window.innerWidth - event.clientX + TOOLTIP_CURSOR_OFFSET
        : event.clientX + TOOLTIP_CURSOR_OFFSET,
      verticalProperty: placeAbove ? 'bottom' : 'top',
      verticalValue: placeAbove
        ? window.innerHeight - event.clientY + TOOLTIP_CURSOR_OFFSET
        : event.clientY + TOOLTIP_CURSOR_OFFSET,
    });
  };

  /** Open full text on button activation, regardless of pointer mode. (Created or modified by CoPilot AI Agent) */
  const handleResponseClick = () => {
    if (isTruncated) {
      setIsOverlayOpen(true);
    }
  };

  const tooltipStyle = tooltipPosition ? {
    [tooltipPosition.horizontalProperty]: tooltipPosition.horizontalValue,
    [tooltipPosition.verticalProperty]: tooltipPosition.verticalValue,
  } : {};

  return (
    <>
      <Box
        component={isTruncated ? 'button' : 'div'}
        type={isTruncated ? 'button' : undefined}
        className={`afh-response-body ${isTruncated ? 'afh-response-body--interactive' : ''}`}
        aria-label={`${endpointLabel} response body${isTruncated ? ', activate to view full text' : ''}`}
        onClick={handleResponseClick}
        onMouseEnter={positionTooltip}
        onMouseMove={positionTooltip}
        onMouseLeave={() => setTooltipPosition(null)}
      >
        {previewText}
      </Box>

      {supportsHover && isTruncated && tooltipPosition && (
        <Box role="tooltip" className="afh-response-tooltip" sx={tooltipStyle}>
          {fullText}
        </Box>
      )}

      <Modal
        open={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
        aria-labelledby={`${endpointLabel}-response-title`}
      >
        <Box
          className="afh-response-overlay"
          onClick={() => setIsOverlayOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <Box className="afh-response-overlay-content">
            <Typography id={`${endpointLabel}-response-title`} variant="h6">Full response body</Typography>
            <Typography>{fullText}</Typography>
            <Typography className="afh-response-overlay-hint">Press Escape or click anywhere to close</Typography>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

/**
 * Resolve the endpoint's user-facing status label.
 * @param {Object} widget - Scenario widget.
 * @param {string} probeKind - head or health.
 * @returns {string} Status label.
 */
function getStatusLabel(widget, probeKind) {
  if (!endpointExists(widget, probeKind)) {
    return 'Missing';
  }
  return widget[probeKind].result?.status?.toUpperCase() || 'NOT RUN';
}

function getProbeDisplayName(probeKind) {
  return probeKind === PROBE_KINDS.HEAD ? 'Head' : 'Health';
}

/**
 * Resolve the endpoint detail shown beneath its metadata.
 * @param {Object} widget - Scenario widget.
 * @param {string} probeKind - head or health.
 * @returns {string} Endpoint detail.
 */
function getEndpointDetail(widget, probeKind) {
  const endpointState = widget[probeKind];
  if (!endpointExists(widget, probeKind)) {
    return `${getProbeDisplayName(probeKind)} endpoint is not available for this application.`;
  }
  if (endpointState.result) {
    return endpointState.result.bodyPreview || 'No response body.';
  }
  if (!widget.probeEnabled) {
    return 'This application is tracked in the catalog but excluded from active probing.';
  }
  if (widget.probeDisabledEnvironments.includes(widget.selectedEnvironment)) {
    return `${getProbeDisplayName(probeKind)} is marked unavailable for ${widget.selectedEnvironment}.`;
  }
  return `Ready to run ${getProbeDisplayName(probeKind)} against ${widget.selectedEnvironment}.`;
}

/**
 * Render one endpoint's health, cadence, and failure notice.
 * @param {Object} props - Component properties.
 * @returns {JSX.Element} Endpoint status panel.
 */
export function EndpointStatusPanel({ widget, probeKind, onClearFailure }) {
  const endpointState = widget[probeKind];
  const status = !endpointExists(widget, probeKind)
    ? 'missing'
    : endpointState.result?.status || 'ready';
  const statusColors = {
    healthy: 'success',
    unhealthy: 'error',
    skipped: 'default',
    ready: 'info',
    missing: 'warning',
  };
  const endpointLabel = `${widget.id}-${probeKind}`;
  const endpointDetail = getEndpointDetail(widget, probeKind);

  return (
    <Box className={`afh-endpoint afh-endpoint--${status}`}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box>
          <Typography className="afh-eyebrow">{probeKind === PROBE_KINDS.HEAD ? 'Head' : 'Health'}</Typography>
          <Typography variant="subtitle2" fontWeight={700}>Endpoint status</Typography>
        </Box>
        <Chip
          size="small"
          color={statusColors[status]}
          label={getStatusLabel(widget, probeKind)}
          sx={status === 'missing' ? {
            backgroundColor: '#F5C542 !important',
            color: '#111111 !important',
            fontWeight: 700,
          } : undefined}
        />
      </Stack>

      <Box className="afh-endpoint-meta">
        <Typography><strong>Last run (UTC):</strong> {endpointState.result?.checkedAt || 'Not run yet'}</Typography>
        <Typography><strong>Status code:</strong> {endpointState.result?.statusCode ?? 'N/A'}</Typography>
        <Typography><strong>Auto refresh:</strong> {probeKind === PROBE_KINDS.HEAD ? 'Every 5 minutes' : 'Every hour'}</Typography>
        <Typography><strong>Next run (UTC):</strong> {endpointState.nextScheduledRunAt || 'No schedule'}</Typography>
      </Box>

      {endpointState.failureNotice && (
        <Alert
          severity="error"
          action={(
            <Button
              aria-label={`Clear ${probeKind} failure notice`}
              color="inherit"
              size="small"
              onClick={() => onClearFailure(widget.id, probeKind)}
            >
              <X size={15} />
            </Button>
          )}
        >
          {endpointState.failureNotice}
        </Alert>
      )}

      {/* Response body interaction added by CoPilot AI Agent */}
      <ResponseBodyPreview fullText={endpointDetail} endpointLabel={endpointLabel} />
    </Box>
  );
}